import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import './analytics.scss';
import { ThemeProvider } from '@alefeducation/ui-elements-library-core/lib/ThemeProvider';
import { TachoMeter } from '@alefeducation/ui-elements-library-icons';
import getLocaleDate from '../../../utils/getLocaleDate';
import { SelectionBlock } from '../../common/SelectionBlock/SelectionBlock';
import assignmentTypes from '../../../utils/assignmentTypes';
import { isNcleAlefAssignment } from '../../../utils/isNcleAlefAssignment';
import Triangle from '../../Triangle/Triangle';
import NewCollapsibleAssignmentDescription from '../NewCollapsibleAssignmentDescription/NewCollapsibleAssignmentDescription';

const NewAssignmentClassAnalytics = ({
  assignmentTitle,
  dueOn,
  stats,
  description,
  attachment,
  gradeable,
  attachmentRequired,
  commentRequired,
  unassignedStudentCount,
  assignmentType,
  maxScore,
  isNcle,
}) => {
  const { t, i18n } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const isNCLE = isNcleAlefAssignment(isNcle, assignmentType);
  const unassignedLabel = isNCLE ? 'Unassigned' : 'Locked';
  const assignedLabel = isNCLE ? 'Assigned' : 'Unlocked';

  const toggleDescription = useCallback(
    event => {
      event.preventDefault();
      event.stopPropagation();
      setIsExpanded(!isExpanded);
    },
    [isExpanded],
  );
  return (
    <ThemeProvider>
      <div className="analytics__wrapper">
        <div className="analytics__container">
          <Triangle
            direction={isExpanded ? 'down' : 'right'}
            onClick={toggleDescription}
            testId="assignment-description-expand-button"
            className={`${assignmentType
              .split('_')
              .join('-')
              .toLowerCase()}-class-dashboard-assignment-detail`}
          />
          <div className="analytics__assignment_details">
            <div
              className="analytics__assignment_title"
              data-testid="assignment-title"
            >
              {assignmentTitle}
            </div>
            <div
              className="analytics__meta_details"
              data-testid="assignment-due-date"
            >
              <span>
                {assignmentType === assignmentTypes.TEACHER
                  ? t('Teacher Assignment')
                  : t('Alef Assignment')}
              </span>
              {dueOn && (
                <>
                  <span>|</span>
                  <span>
                    {t('Due Date')}
                    {' : '}
                    {getLocaleDate({ date: dueOn, language: i18n.language })}
                  </span>
                </>
              )}
            </div>
          </div>
          <div
            className="analytics__assignment_analytics"
            style={{ justifyContent: 'flex-end' }}
          >
            <SelectionBlock
              variant="locked"
              justifyContent="center"
              mr="8"
              active={true}
              title={t(unassignedLabel)}
              value={unassignedStudentCount}
            />
            <SelectionBlock
              variant="default"
              flexDirection="row"
              alignItems="center"
            >
              <SelectionBlock
                variant="noBorder"
                size="sm"
                mr="8"
                value={stats.assignedStudentsCount}
                title={t(assignedLabel)}
              />
              <SelectionBlock
                variant="completed"
                size="sm"
                mr="8"
                active={false}
                value={stats.submissionsCount}
                title={t('Submitted')}
              />
              <SelectionBlock
                variant="inProgress"
                size="sm"
                mr="8"
                active={false}
                value={stats.evaluatedCount}
                title={t('Evaluated')}
              />
              <SelectionBlock
                variant="notStarted"
                size="sm"
                active={false}
                title={t('Returned')}
                value={stats.returnedCount}
              />
            </SelectionBlock>
            {gradeable && (
              <div
                className="analytics__class_average"
                data-testid="assignment-stats__class-average"
              >
                <div className="analytics__class_average__heading">
                  <TachoMeter fontSize={24} />
                  {stats.average ? `${stats.average}%` : '--'}
                </div>
                <div className="analytics__class_average__sub_heading">
                  {t('Class Average')}
                </div>
              </div>
            )}
          </div>
        </div>
        <NewCollapsibleAssignmentDescription
          description={description}
          attachment={attachment}
          commentRequired={commentRequired}
          attachmentRequired={attachmentRequired}
          isExpanded={isExpanded}
          maxScore={maxScore}
          gradable={gradeable}
        />
      </div>
    </ThemeProvider>
  );
};

NewAssignmentClassAnalytics.defaultProps = {
  attachment: null,
  maxScore: null,
  isNcle: false,
};

NewAssignmentClassAnalytics.propTypes = {
  assignmentTitle: PropTypes.string.isRequired,
  dueOn: PropTypes.string.isRequired,
  maxScore: PropTypes.number,
  stats: PropTypes.shape({
    assignedStudentsCount: PropTypes.number,
    submissionsCount: PropTypes.number,
    evaluatedCount: PropTypes.number,
    returnedCount: PropTypes.number,
    average: PropTypes.number,
  }).isRequired,
  description: PropTypes.string.isRequired,
  attachment: PropTypes.object,
  attachmentRequired: PropTypes.bool.isRequired,
  commentRequired: PropTypes.bool.isRequired,
  gradeable: PropTypes.bool.isRequired,
  unassignedStudentCount: PropTypes.number.isRequired,
  assignmentType: PropTypes.string.isRequired,
  isNcle: PropTypes.bool,
};

export default NewAssignmentClassAnalytics;
